import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const EMPTY = { name: "", slug: "", description: "", imageUrl: "", order: 0, isActive: true, subTypes: [] };

export default function AdminCategories() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const refresh = () =>
        api.get("/categories")
            .then(({ data }) => setItems(data || []))
            .catch(() => setItems([]));

    useEffect(() => { refresh(); }, []);

    const startCreate = () => { setEditing({ ...EMPTY }); setOpen(true); };
    const startEdit = (c) => { setEditing({ ...EMPTY, ...c }); setOpen(true); };
    const close = () => { setOpen(false); setEditing(null); };

    const save = async (e) => {
        e.preventDefault();
        if (!editing) return;
        setBusy(true);
        try {
            const subTypes = Array.isArray(editing.subTypes)
                ? editing.subTypes
                : String(editing.subTypes || "")
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean);
            const payload = { ...editing, order: Number(editing.order) || 0, subTypes };
            if (editing.id) {
                await api.patch(`/admin/categories/${editing.id}`, payload);
                toast.success("Category updated");
            } else {
                await api.post("/admin/categories", payload);
                toast.success("Category created");
            }
            close();
            refresh();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this category?")) return;
        try {
            await api.delete(`/admin/categories/${id}`);
            toast.success("Category deleted");
            refresh();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to delete");
        }
    };

    return (
        <div data-testid="admin-categories-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <span className="label-eyebrow">Curation</span>
                    <h1 className="mt-2 font-display text-4xl text-inkDark">Categories</h1>
                    <p className="mt-1 text-inkMuted">{items.length} categories in your boutique.</p>
                </div>
                <button onClick={startCreate} className="btn-pill text-sm" data-testid="admin-add-category-button">
                    <Plus size={14} /> Add category
                </button>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((c) => (
                    <div key={c.id} className="bg-ivory rounded-2xl border border-inkDark/5 overflow-hidden flex" data-testid={`admin-category-${c.slug}`}>
                        <div className="w-32 bg-beige">
                            {c.imageUrl ? <img src={c.imageUrl} alt={c.name} className="w-full h-full object-cover" /> : null}
                        </div>
                        <div className="flex-1 p-4 flex flex-col">
                            <div className="font-display text-lg text-inkDark">{c.name}</div>
                            <div className="label-eyebrow text-[10px] mt-1">/{c.slug}</div>
                            <div className="mt-auto pt-3 flex items-center gap-2">
                                <button onClick={() => startEdit(c)} className="p-2 rounded-lg hover:bg-beige" data-testid={`admin-category-edit-${c.slug}`}>
                                    <Pencil size={14} />
                                </button>
                                <button onClick={() => remove(c.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" data-testid={`admin-category-delete-${c.slug}`}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {open && editing ? (
                <div className="fixed inset-0 z-50 bg-inkDark/40 flex items-center justify-center p-4" onClick={close}>
                    <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-ivory w-full max-w-xl rounded-2xl shadow-lift">
                        <div className="p-6 border-b border-inkDark/5">
                            <span className="label-eyebrow">{editing.id ? "Edit" : "New"}</span>
                            <h2 className="font-display text-2xl">{editing.id ? "Edit category" : "Add category"}</h2>
                        </div>
                        <div className="p-6 grid grid-cols-1 gap-4">
                            <label>
                                <span className="label-eyebrow">Name</span>
                                <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="admin-category-name-input" />
                            </label>
                            <label>
                                <span className="label-eyebrow">Slug</span>
                                <input value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" placeholder="auto-generated" />
                            </label>
                            <label>
                                <span className="label-eyebrow">Description</span>
                                <textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </label>
                            <label>
                                <span className="label-eyebrow">Image URL</span>
                                <input value={editing.imageUrl || ""} onChange={(e) => setEditing({ ...editing, imageUrl: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </label>
                            <label>
                                <span className="label-eyebrow">Order</span>
                                <input type="number" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </label>
                            <label>
                                <span className="label-eyebrow">Sub-types (comma separated)</span>
                                <input
                                    value={Array.isArray(editing.subTypes) ? editing.subTypes.join(", ") : (editing.subTypes || "")}
                                    onChange={(e) => setEditing({ ...editing, subTypes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                                    placeholder="e.g. Jewellery, Handbags & Purses"
                                    className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5"
                                    data-testid="admin-category-subtypes-input"
                                />
                                <p className="mt-1 text-xs text-inkMuted">Shown as filter chips on the category page.</p>
                            </label>
                        </div>
                        <div className="p-6 border-t border-inkDark/5 flex justify-end gap-3">
                            <button type="button" onClick={close} className="btn-ghost text-sm">Cancel</button>
                            <button type="submit" disabled={busy} className="btn-pill text-sm" data-testid="admin-category-save-button">{busy ? "Saving…" : "Save"}</button>
                        </div>
                    </form>
                </div>
            ) : null}
        </div>
    );
}
